/**
 * Accessible Shopify predictive search for the custom theme.
 *
 * Progressive enhancement: every form remains a normal GET /search form when
 * JavaScript or the predictive endpoint is unavailable.
 */
(() => {
  'use strict';

  const MINIMUM_QUERY_LENGTH = 2;
  const DEBOUNCE_DELAY = 250;
  const PRODUCT_LIMIT = 6;

  class PredictiveSearch {
    constructor(root) {
      this.root = root;
      this.form = root.querySelector('[data-predictive-search-form]');
      this.input = root.querySelector('[data-predictive-search-input]');
      this.results = root.querySelector('[data-predictive-search-results]');
      this.status = root.querySelector('[data-predictive-search-status]');

      if (!this.form || !this.input || !this.results || !this.status) {
        return;
      }

      this.activeIndex = -1;
      this.debounceTimer = null;
      this.requestController = null;
      this.requestSequence = 0;
      this.options = [];
      this.cache = new Map();
      this.currencyCode =
        root.dataset.currencyCode ||
        window.Shopify?.currency?.active ||
        'USD';
      this.locale = document.documentElement.lang || 'en-US';

      this.bindEvents();
    }

    bindEvents() {
      this.input.addEventListener('input', () => this.handleInput());
      this.input.addEventListener('keydown', (event) => this.handleKeydown(event));
      this.input.addEventListener('focus', () => this.handleFocus());

      this.form.addEventListener('submit', () => {
        this.clearPendingRequest();
        this.close();
      });

      this.results.addEventListener('pointermove', (event) => {
        const option = event.target.closest('[data-predictive-search-option]');
        if (!option || !this.results.contains(option)) {
          return;
        }

        this.setActiveIndex(this.options.indexOf(option), false);
      });

      this.results.addEventListener('pointerleave', () => {
        this.setActiveIndex(-1, false);
      });

      document.addEventListener('pointerdown', (event) => {
        if (!this.root.contains(event.target)) {
          this.close();
        }
      });
    }

    handleInput() {
      const query = this.query;

      this.clearPendingRequest();
      this.setActiveIndex(-1, false);

      if (query.length < MINIMUM_QUERY_LENGTH) {
        this.close();
        this.updateStatus('');
        return;
      }

      if (this.cache.has(query)) {
        this.renderProducts(this.cache.get(query), query);
        return;
      }

      this.renderLoading(query);
      this.debounceTimer = window.setTimeout(() => {
        this.fetchResults(query);
      }, DEBOUNCE_DELAY);
    }

    handleFocus() {
      const query = this.query;

      if (
        query.length >= MINIMUM_QUERY_LENGTH &&
        this.results.hidden &&
        this.cache.has(query)
      ) {
        this.renderProducts(this.cache.get(query), query);
      }
    }

    handleKeydown(event) {
      if (event.key === 'Escape') {
        if (!this.results.hidden) {
          event.preventDefault();
          event.stopPropagation();
          this.close();
        }
        return;
      }

      if (this.results.hidden || this.options.length === 0) {
        return;
      }

      if (event.key === 'ArrowDown') {
        event.preventDefault();
        this.moveSelection(1);
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        this.moveSelection(-1);
      } else if (event.key === 'Enter' && this.activeIndex >= 0) {
        event.preventDefault();
        this.options[this.activeIndex].click();
      }
    }

    async fetchResults(query) {
      const requestSequence = ++this.requestSequence;
      const requestController = new AbortController();
      this.requestController = requestController;

      try {
        const response = await fetch(this.predictiveSearchUrl(query), {
          headers: {
            Accept: 'application/json'
          },
          signal: requestController.signal
        });

        if (!response.ok) {
          throw new Error(`Predictive search returned ${response.status}`);
        }

        const payload = await response.json();
        const products = payload?.resources?.results?.products || [];
        const visibleProducts = products
          .filter((product) => {
            return (
              product &&
              product.title &&
              product.url &&
              !product.title.includes('(Optional)')
            );
          })
          .slice(0, PRODUCT_LIMIT);

        if (
          requestSequence !== this.requestSequence ||
          query !== this.query
        ) {
          return;
        }

        this.cache.set(query, visibleProducts);
        this.renderProducts(visibleProducts, query);
      } catch (error) {
        if (error.name === 'AbortError') {
          return;
        }

        if (
          requestSequence === this.requestSequence &&
          query === this.query
        ) {
          this.renderError(query);
        }
      } finally {
        if (this.requestController === requestController) {
          this.requestController = null;
        }
      }
    }

    predictiveSearchUrl(query) {
      const route = window.routes?.predictive_search_url || '/search/suggest';
      const url = new URL(route, window.location.origin);

      if (!url.pathname.endsWith('.json')) {
        url.pathname = `${url.pathname}.json`;
      }

      url.searchParams.set('q', query);
      url.searchParams.set('resources[type]', 'product');
      url.searchParams.set('resources[limit]', String(PRODUCT_LIMIT));
      url.searchParams.set(
        'resources[options][unavailable_products]',
        'last'
      );

      return url.toString();
    }

    renderProducts(products, query) {
      this.results.replaceChildren();

      if (products.length === 0) {
        this.renderState(
          `No product suggestions found for “${query}”.`,
          query
        );
        this.updateStatus(
          `No product suggestions found for ${query}. Press Enter to search all results.`
        );
        return;
      }

      const heading = document.createElement('p');
      heading.className = 'predictive-search__heading';
      heading.textContent = 'Suggested products';

      const list = document.createElement('div');
      list.className = 'predictive-search__list';

      products.forEach((product, index) => {
        const option = this.createProductOption(product, index);
        if (option) {
          list.appendChild(option);
        }
      });

      this.results.append(heading, list, this.createViewAllOption(query));
      this.open();
      this.collectOptions();
      this.updateStatus(
        `${products.length} product suggestions available for ${query}. Use the up and down arrow keys to review them.`
      );
    }

    createProductOption(product, index) {
      const productUrl = this.safeProductUrl(product.url);
      if (!productUrl) {
        return null;
      }

      const option = document.createElement('a');
      option.className = 'predictive-search__option';
      option.href = productUrl;
      option.id = `${this.results.id}-option-${index}`;
      option.setAttribute('role', 'option');
      option.setAttribute('aria-selected', 'false');
      option.setAttribute('data-predictive-search-option', '');

      const image = document.createElement('div');
      image.className = 'predictive-search__image';

      const imageUrl = this.safeImageUrl(
        product.image || product.featured_image?.url
      );
      if (imageUrl) {
        const imageElement = document.createElement('img');
        imageElement.src = imageUrl;
        imageElement.alt = '';
        imageElement.width = 64;
        imageElement.height = 64;
        imageElement.loading = 'lazy';
        image.appendChild(imageElement);
      }

      const details = document.createElement('div');
      details.className = 'predictive-search__details';

      const title = document.createElement('p');
      title.className = 'predictive-search__title';
      title.textContent = product.title;

      const meta = document.createElement('div');
      meta.className = 'predictive-search__meta';

      const price = this.formatPrice(product.price);
      if (price) {
        const priceElement = document.createElement('span');
        priceElement.className = 'predictive-search__price';
        priceElement.textContent = price;
        meta.appendChild(priceElement);
      }

      if (product.available === false) {
        const availability = document.createElement('span');
        availability.className =
          'predictive-search__availability predictive-search__availability--unavailable';
        availability.textContent = 'Sold out';
        meta.appendChild(availability);
      }

      details.append(title, meta);
      option.append(image, details);

      return option;
    }

    renderLoading(query) {
      this.results.replaceChildren();

      const state = document.createElement('div');
      state.className = 'predictive-search__state';

      const spinner = document.createElement('span');
      spinner.className = 'predictive-search__spinner';
      spinner.setAttribute('aria-hidden', 'true');

      const message = document.createElement('span');
      message.textContent = `Searching for “${query}”…`;

      state.append(spinner, message);
      this.results.appendChild(state);
      this.open();
      this.collectOptions();
      this.updateStatus(`Searching for ${query}.`);
    }

    renderError(query) {
      this.renderState(
        'Suggestions are temporarily unavailable. You can still search the full catalog.',
        query
      );
      this.updateStatus(
        'Product suggestions are temporarily unavailable. Press Enter to search the full catalog.'
      );
    }

    renderState(message, query) {
      this.results.replaceChildren();

      const state = document.createElement('div');
      state.className = 'predictive-search__state';
      state.textContent = message;

      this.results.append(state, this.createViewAllOption(query));
      this.open();
      this.collectOptions();
    }

    createViewAllOption(query) {
      const option = document.createElement('a');
      option.className = 'predictive-search__view-all';
      option.href = this.fullSearchUrl(query);
      option.id = `${this.results.id}-view-all`;
      option.setAttribute('role', 'option');
      option.setAttribute('aria-selected', 'false');
      option.setAttribute('data-predictive-search-option', '');
      option.textContent = `View all results for “${query}”`;
      return option;
    }

    fullSearchUrl(query) {
      const url = new URL(this.form.action, window.location.origin);
      url.searchParams.set('q', query);
      return url.toString();
    }

    safeProductUrl(value) {
      try {
        const url = new URL(value, window.location.origin);
        return url.origin === window.location.origin ? url.toString() : null;
      } catch (_error) {
        return null;
      }
    }

    safeImageUrl(value) {
      if (!value) {
        return null;
      }

      try {
        const url = new URL(value, window.location.origin);
        return ['http:', 'https:'].includes(url.protocol)
          ? url.toString()
          : null;
      } catch (_error) {
        return null;
      }
    }

    formatPrice(value) {
      const amount = Number.parseFloat(value);
      if (!Number.isFinite(amount)) {
        return '';
      }

      try {
        return new Intl.NumberFormat(this.locale, {
          style: 'currency',
          currency: this.currencyCode,
          currencyDisplay: 'symbol'
        }).format(amount);
      } catch (_error) {
        return `$${amount.toFixed(2)}`;
      }
    }

    moveSelection(direction) {
      if (this.options.length === 0) {
        return;
      }

      let nextIndex = this.activeIndex + direction;
      if (nextIndex < 0) {
        nextIndex = this.options.length - 1;
      } else if (nextIndex >= this.options.length) {
        nextIndex = 0;
      }

      this.setActiveIndex(nextIndex, true);
    }

    setActiveIndex(index, shouldScroll) {
      this.options.forEach((option, optionIndex) => {
        const active = optionIndex === index;
        option.classList.toggle('is-active', active);
        option.setAttribute('aria-selected', String(active));
      });

      this.activeIndex = index;

      if (index < 0 || !this.options[index]) {
        this.input.removeAttribute('aria-activedescendant');
        return;
      }

      const activeOption = this.options[index];
      this.input.setAttribute('aria-activedescendant', activeOption.id);

      if (shouldScroll) {
        activeOption.scrollIntoView({ block: 'nearest' });
      }
    }

    collectOptions() {
      this.options = Array.from(
        this.results.querySelectorAll('[data-predictive-search-option]')
      );
      this.setActiveIndex(-1, false);
    }

    open() {
      this.results.hidden = false;
      this.input.setAttribute('aria-expanded', 'true');
    }

    close() {
      this.results.hidden = true;
      this.input.setAttribute('aria-expanded', 'false');
      this.setActiveIndex(-1, false);
    }

    clearPendingRequest() {
      if (this.debounceTimer) {
        window.clearTimeout(this.debounceTimer);
        this.debounceTimer = null;
      }

      if (this.requestController) {
        this.requestController.abort();
        this.requestController = null;
      }
    }

    updateStatus(message) {
      this.status.textContent = message;
    }

    get query() {
      return this.input.value.trim();
    }
  }

  const initializePredictiveSearch = () => {
    document
      .querySelectorAll('[data-predictive-search]')
      .forEach((root) => new PredictiveSearch(root));
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializePredictiveSearch);
  } else {
    initializePredictiveSearch();
  }
})();
